import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import cx from 'classnames';

import styles from './MarkdownButton.module.scss';
import { CopyIcon, CheckIcon } from '../CodeBlockV2/icons';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events, MARKDOWN_BUTTON_TRACKING_IDS } from 'src/lib/trackingEvents';
import { isMarkdownExcluded } from 'src/lib/markdown-config';

/**
 * PROTOTYPE 2: Inline Header Action
 *
 * A utility button positioned in the page header area,
 * providing quick access to markdown view and copy from the top of the page.
 */
const MarkdownButton = () => {
  const { asPath, basePath } = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const track = useTrack();

  // Convert /docs/link/overview to /docs/link/overview/index.html.md
  // Also handles /core-exchange/docs/ paths
  const getMarkdownUrl = () => {
    let path = asPath;
    // Remove trailing slash
    if (path.endsWith('/')) {
      path = path.substring(0, path.length - 1);
    }
    // Add index.html.md using current origin and basePath for environment-aware URLs
    // basePath includes commit hash on staging (e.g., /1c36fa) and is empty on production
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://plaid.com';
    return `${origin}${basePath}${path}/index.html.md`;
  };

  const markdownUrl = getMarkdownUrl();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleCopyMarkdown = async () => {
    try {
      // Fetch markdown directly from the public URL
      const response = await fetch(markdownUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch markdown: ${response.status}`);
      }

      const markdownContent = await response.text();

      await navigator.clipboard.writeText(markdownContent);
      setCopySuccess(true);

      track({
        type: Events.MARKDOWN_BUTTON_CLICKED,
        payload: { markdownAction: MARKDOWN_BUTTON_TRACKING_IDS.BUTTON_COPIED },
      });

      setTimeout(() => {
        setCopySuccess(false);
        setIsOpen(false);
      }, 2000);
    } catch (err) {
      track({
        type: Events.MARKDOWN_BUTTON_CLICKED,
        payload: {
          markdownAction: MARKDOWN_BUTTON_TRACKING_IDS.BUTTON_COPY_FAILED,
          error: err instanceof Error ? err.message : 'Unknown error',
        },
      });

      window.open(markdownUrl, '_blank');
      alert(
        'Could not copy automatically. The markdown file has been opened in a new tab. Please copy it manually.',
      );
      setIsOpen(false);
    }
  };

  if (isMarkdownExcluded(asPath)) {
    return null;
  }

  return (
    <div className={styles.markdownButtonContainer} ref={dropdownRef}>
      <button
        type='button'
        className={styles.markdownButton}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const newIsOpen = !isOpen;
          setIsOpen(newIsOpen);

          if (newIsOpen) {
            track({
              type: Events.MARKDOWN_BUTTON_CLICKED,
              payload: {
                markdownAction: MARKDOWN_BUTTON_TRACKING_IDS.BUTTON_OPENED,
              },
            });
          }
        }}
        onMouseDown={(e) => {
          // Prevent focus to avoid triggering Schema scroll logic
          e.preventDefault();
        }}
        aria-label='Markdown options'
        aria-expanded={isOpen}
      >
        <svg
          className={styles.icon}
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
        >
          <path
            d='M14 4.5V14C14 14.2652 13.8946 14.5196 13.7071 14.7071C13.5196 14.8946 13.2652 15 13 15H3C2.73478 15 2.48043 14.8946 2.29289 14.7071C2.10536 14.5196 2 14.2652 2 14V2C2 1.73478 2.10536 1.48043 2.29289 1.29289C2.48043 1.10536 2.73478 1 3 1H11.5L14 4.5Z'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M11 1V5H14'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M5 8H11M5 11H11'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
        <span className={styles.label}>Markdown</span>
        <svg
          className={cx(styles.chevron, isOpen && styles.chevronOpen)}
          width='12'
          height='12'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M4 6L8 10L12 6'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <a
            href={markdownUrl}
            className={styles.dropdownItem}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              track({
                type: Events.MARKDOWN_BUTTON_CLICKED,
                payload: {
                  markdownAction:
                    MARKDOWN_BUTTON_TRACKING_IDS.BUTTON_VIEWED_IN_NEW_PAGE,
                },
              });
            }}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M13 9V13H3V9M8 11V3M8 3L11 6M8 3L5 6'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            View page as markdown
          </a>
          <button
            className={styles.dropdownItem}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCopyMarkdown();
            }}
            onMouseDown={(e) => {
              // Prevent focus to avoid triggering Schema scroll logic
              e.preventDefault();
            }}
            type='button'
          >
            {copySuccess ? <CheckIcon /> : <CopyIcon />}
            {copySuccess ? 'Copied!' : 'Copy page as markdown'}
          </button>
        </div>
      )}
    </div>
  );
};

MarkdownButton.displayName = 'MarkdownButton';
export default MarkdownButton;
